export const INDEXED_DB_SAVE_DATABASE_NAME = "hover-idle-save-db";
export const INDEXED_DB_SAVE_DATABASE_VERSION = 1;
export const INDEXED_DB_SAVE_STORE_NAME = "hoverIdleSaves";
export const INDEXED_DB_LATEST_SAVE_KEY = "latest";

export interface IndexedDbSaveRecord<TData = unknown> {
  readonly id: string;
  readonly savedAt: number;
  readonly data: TData;
}

export async function readFirstIndexedDbSaveRecord(
  keys: readonly string[],
): Promise<unknown | undefined> {
  const database = await openIndexedDbSaveDatabase();
  try {
    const transaction = database.transaction(
      INDEXED_DB_SAVE_STORE_NAME,
      "readonly",
    );
    const store = transaction.objectStore(INDEXED_DB_SAVE_STORE_NAME);

    for (const key of keys) {
      const record = await requestToPromise<unknown>(store.get(key));
      if (record !== undefined) {
        return record;
      }
    }

    return undefined;
  } finally {
    database.close();
  }
}

export async function writeIndexedDbSaveRecord(
  record: IndexedDbSaveRecord,
): Promise<void> {
  const database = await openIndexedDbSaveDatabase();
  try {
    const transaction = database.transaction(
      INDEXED_DB_SAVE_STORE_NAME,
      "readwrite",
    );
    const transactionDone = transactionToPromise(transaction);
    const store = transaction.objectStore(INDEXED_DB_SAVE_STORE_NAME);
    await requestToPromise(store.put(record));
    await transactionDone;
  } finally {
    database.close();
  }
}

export type IndexedDbSaveRecordTransformResult =
  | { readonly ok: true; readonly record: IndexedDbSaveRecord }
  | { readonly ok: false; readonly reason: unknown };

export async function transformIndexedDbSaveRecord(
  readKeys: readonly string[],
  transform: (
    current: unknown | undefined,
  ) => IndexedDbSaveRecordTransformResult,
): Promise<
  { readonly ok: true } | { readonly ok: false; readonly reason: unknown }
> {
  const database = await openIndexedDbSaveDatabase();
  try {
    const transaction = database.transaction(
      INDEXED_DB_SAVE_STORE_NAME,
      "readwrite",
    );
    const store = transaction.objectStore(INDEXED_DB_SAVE_STORE_NAME);
    let current: unknown | undefined;
    for (const key of readKeys) {
      current = await requestToPromise<unknown>(store.get(key));
      if (current !== undefined) break;
    }
    const transformed = transform(current);
    if (!transformed.ok) {
      return transformed;
    }
    const transactionDone = transactionToPromise(transaction);
    await requestToPromise(store.put(transformed.record));
    await transactionDone;
    return { ok: true };
  } finally {
    database.close();
  }
}

function openIndexedDbSaveDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = globalThis.indexedDB.open(
      INDEXED_DB_SAVE_DATABASE_NAME,
      INDEXED_DB_SAVE_DATABASE_VERSION,
    );

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(INDEXED_DB_SAVE_STORE_NAME)) {
        database.createObjectStore(INDEXED_DB_SAVE_STORE_NAME, {
          keyPath: "id",
        });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB open failed"));
    request.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}
